import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Grid,
  TextField,
  Switch,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Autocomplete,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import ChartWidget, { LINE_INTERPOLATION_OPTIONS } from './ChartWidget';
import ChartPreviewAxesSeriesPanel from './ChartPreviewAxesSeriesPanel';
import HeatmapGridWidget from './HeatmapGridWidget';
import MapWidget from './MapWidget';
import DataTable from './DataTable';
import CalculatedColumnsEditor from './CalculatedColumnsEditor';
import { applyCommittedHeatmapPositionColumns, resolveCommittedHeatmapPositions } from '../utils/imageDensityConfig';
import { appendHeatmapPositionVisibleColumns } from '../utils/imageDensityPoints';
import {
  imageCarouselVizFieldsFromConfig,
  resolveImageCarouselConfig,
} from '../utils/imageCarouselConfig';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import vsaasTheme from '../theme';
import DateDisplayFormatControl from './chartBuilder/DateDisplayFormatControl';
import {
  resolveXDateBucket,
  resolveXDateDisplayFormat,
  resolveLabelDateBucket,
  resolveLabelDateDisplayFormat,
} from '../utils/dateBucketFormat';
import { supportsFillTimeAxisGaps } from '../utils/timeAxisGaps';
import {
  DEFAULT_PIE_LABEL_POSITION,
  PIE_LABEL_POSITION_OPTIONS,
  PIE_LEGEND_FONT_SIZE_OPTIONS,
  pieLabelsInLegend as isPieLegendLabelMode,
} from '../utils/pieChartOptions';

function isLikelyDateColumn(col, sampleValue, configuredFormats) {
  if (configuredFormats?.[col]) return true;
  if (/time|date|timestamp|created|updated|fecha/i.test(col)) return true;
  const s = String(sampleValue ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:)/.test(s);
}

const CHART_TYPES = [
  { value: 'line', label: 'Línea' },
  { value: 'bar', label: 'Barras' },
  { value: 'pie', label: 'Pastel' },
  { value: 'doughnut', label: 'Dona' },
  { value: 'radar', label: 'Radar (spider)' },
  { value: 'scatter', label: 'Dispersión' },
  { value: 'heatmapGrid', label: 'Heatmap sobre imagen' },
  { value: 'map', label: 'Mapa' },
  { value: 'table', label: 'Tabla' },
  { value: 'tablePlus', label: 'Tabla+' },
  { value: 'imageCarousel', label: 'Carrusel de imágenes' },
];

const DEFAULT_HEATMAP_GRID_CONFIG = {
  backgroundImageUrl: '',
  gridRows: 3,
  gridCols: 3,
  showValues: true,
  showFilterValueName: false,
  valueUnit: '',
  valueColumn: '',
  filterColumn: '',
  secondaryValueColumn: '',
  cellAssignments: {},
};

const DEFAULT_MAP_CONFIG = {
  latColumn: '',
  lngColumn: '',
  addressColumn: '',
  comunaColumn: '',
  regionColumn: '',
  countryColumn: '',
  labelColumn: '',
  valueColumn: '',
  valueMapping: 'size', // 'size' | 'color' | 'both': usar valor para tamaño, color o ambos
  valueColorMin: '#2196f3',
  valueColorMax: '#f44336',
  popupColumns: [],
  markerStyle: 'circle',
  markerColor: '#1976d2',
  markerSize: 'medium',
  heatmapMode: false,
  heatmapUseValue: false, // false = calor por cantidad de puntos; true = calor también por valor (zonas con más valor más rojas)
};

const MAP_VALUE_MAPPING_OPTIONS = [
  { value: 'size', label: 'Tamaño del marcador' },
  { value: 'color', label: 'Color del marcador' },
  { value: 'both', label: 'Tamaño y color' },
];

const MAP_MARKER_STYLES = [
  { value: 'circle', label: 'Círculo' },
  { value: 'pin', label: 'Pin' },
  { value: 'square', label: 'Cuadrado' },
];

const MAP_MARKER_COLORS = [
  { value: '#1976d2', label: 'Azul' },
  { value: '#d32f2f', label: 'Rojo' },
  { value: '#388e3c', label: 'Verde' },
  { value: '#f57c00', label: 'Naranja' },
  { value: '#7b1fa2', label: 'Violeta' },
  { value: '#0097a7', label: 'Cian' },
  { value: '#c62828', label: 'Granate' },
  { value: '#212121', label: 'Negro' },
];

const MAP_MARKER_SIZES = [
  { value: 'small', label: 'Pequeño' },
  { value: 'medium', label: 'Mediano' },
  { value: 'large', label: 'Grande' },
];

const DEFAULT_DYNAMIC_FILTER = {
  enabled: false,
  paramName: '',
  filterType: 'select',
  optionSource: 'query',
  optionsQuery: '',
  labelColumn: '',
  valueColumn: '',
  staticOptions: [],
  selectedValue: '',
  allowExecuteWithoutSelection: false,
};

const normalizeDynamicFiltersFromConfig = (cfg) => {
  if (Array.isArray(cfg?.dynamicFilters)) {
    return cfg.dynamicFilters
      .filter((f) => f && typeof f === 'object')
      .map((f) => ({ ...DEFAULT_DYNAMIC_FILTER, ...f }));
  }
  if (cfg?.dynamicFilter && typeof cfg.dynamicFilter === 'object') {
    return [{ ...DEFAULT_DYNAMIC_FILTER, ...cfg.dynamicFilter }];
  }
  return [];
};

/**
 * Extrae mapa alias -> nombre de tabla desde el SQL (FROM y JOIN).
 * @param {string} sql
 * @returns {Record<string, string>} alias -> table (e.g. "p" -> "dbo.MAEPR")
 */
function parseTableAliases(sql) {
  const map = {};
  const fromRegex = /(?:FROM|JOIN)\s+([\w.[\]]+)\s+(?:AS\s+)?(\w+)\b/gi;
  let m;
  while ((m = fromRegex.exec(sql)) !== null) {
    const table = m[1].trim();
    const alias = m[2].trim();
    if (alias && !map[alias]) map[alias] = table;
  }
  return map;
}

/**
 * Detecta en el texto SQL comparaciones con literales entre comillas para proponer como filtro dinámico.
 * Incluye alias.columna y sugiere una query para cargar todos los valores posibles.
 * filterOptionSources: array de { columnNames, optionsQuery, valueColumn, labelColumn } (configuración desde admin / system).
 * @returns {Array<{ columnName: string, values: string[], type: string, snippet: string, suggestedOptionsQuery?: string, suggestedValueColumn?: string, suggestedLabelColumn?: string }>}
 */
export function detectFilterCandidates(queryText, filterOptionSources = []) {
  if (!queryText || typeof queryText !== 'string') return [];
  const trimmed = queryText.trim();
  const aliasToTable = parseTableAliases(trimmed);
  const candidates = [];
  const seen = new Set();
  const sources = Array.isArray(filterOptionSources) ? filterOptionSources : [];
  const escReg = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  function colNorm(c) {
    return (c || '').replace(/^\[|\]$/g, '');
  }

  function resolveTable(alias) {
    if (!alias) return null;
    return aliasToTable[alias] || null;
  }

  function isDateLikeColumn(columnName) {
    const c = String(colNorm(columnName) || '').toUpperCase();
    return /FECHA|FEC|DATE|TIME|PERIODO|PERIOD|MES|ANIO|ANO|EMISION|EMISI|VENC|CREACION|CREADO/.test(c);
  }

  function findRuleForColumn(columnName) {
    const cn = colNorm(columnName);
    if (!cn) return null;
    const upper = cn.toUpperCase();
    return sources.find(
      (r) =>
        Array.isArray(r.columnNames) &&
        r.columnNames.some((n) => String(n).toUpperCase() === upper)
    ) || null;
  }

  function suggestOptionsQuery(columnName, tableName) {
    if (!columnName) return null;
    const cn = colNorm(columnName);
    const rule = findRuleForColumn(columnName);
    if (rule && (rule.optionsQuery || '').trim()) return rule.optionsQuery.trim();
    if (!tableName) return null;
    return `SELECT DISTINCT ${cn} FROM ${tableName}`;
  }

  function suggestLabelColumn(columnName, tableName) {
    const cn = colNorm(columnName);
    const rule = findRuleForColumn(columnName);
    if (rule && (rule.labelColumn || '').trim()) return rule.labelColumn.trim();
    return cn;
  }

  function suggestValueColumn(columnName, tableName) {
    const cn = colNorm(columnName);
    const rule = findRuleForColumn(columnName);
    if (rule && (rule.valueColumn || '').trim()) return rule.valueColumn.trim();
    return cn;
  }

  // Nombre de parámetro único para la regla: si la regla tiene columnNames [KOPR, KOPRCT], usamos valueColumn (ej. KOPR) para todos.
  function canonicalParamForColumn(columnName) {
    const rule = findRuleForColumn(columnName);
    if (rule && (rule.valueColumn || '').trim()) return String(rule.valueColumn).trim();
    if (rule && Array.isArray(rule.columnNames) && rule.columnNames.length > 0) return String(rule.columnNames[0]).trim();
    return columnName;
  }

  function add(alias, col, values, type, snippet) {
    const columnName = colNorm(col);
    if (!columnName) return;
    const key = `${alias || ''}.${columnName}|${type}`;
    if (seen.has(key)) return;
    seen.add(key);
    const tableName = resolveTable(alias);
    const suggestedOptionsQuery = suggestOptionsQuery(columnName, tableName);
    const suggestedValueColumn = suggestValueColumn(columnName, tableName);
    const suggestedLabelColumn = suggestLabelColumn(columnName, tableName);
    const canonicalParamName = canonicalParamForColumn(columnName);
    candidates.push({
      columnName,
      canonicalParamName,
      values,
      type,
      snippet,
      suggestedOptionsQuery: suggestedOptionsQuery || undefined,
      suggestedValueColumn,
      suggestedLabelColumn,
    });
  }

  // alias.Columna = 'literal' o Columna = 'literal' (comillas simples)
  let m;
  const eqSingle = /(?:(\w+)\.)?(\w+|\[\w+\])\s*=\s*'([^']*)'/gi;
  while ((m = eqSingle.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], '=', `${m[1] ? m[1] + '.' : ''}${m[2]} = '${m[3]}'`);
  }
  const eqDouble = /(?:(\w+)\.)?(\w+|\[\w+\])\s*=\s*"([^"]*)"/gi;
  while ((m = eqDouble.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], '=', `${m[1] ? m[1] + '.' : ''}${m[2]} = "${m[3]}"`);
  }
  // LTRIM(RTRIM(alias.Columna)) = 'literal' o = "literal"
  const ltrimRtrimEqSingle = /LTRIM\s*\(\s*RTRIM\s*\(\s*(?:(\w+)\.)?(\w+)\s*\)\s*\)\s*=\s*'([^']*)'/gi;
  while ((m = ltrimRtrimEqSingle.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], '=', `LTRIM(RTRIM(${m[1] ? m[1] + '.' : ''}${m[2]})) = '${m[3]}'`);
  }
  const ltrimRtrimEqDouble = /LTRIM\s*\(\s*RTRIM\s*\(\s*(?:(\w+)\.)?(\w+)\s*\)\s*\)\s*=\s*"([^"]*)"/gi;
  while ((m = ltrimRtrimEqDouble.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], '=', `LTRIM(RTRIM(${m[1] ? m[1] + '.' : ''}${m[2]})) = "${m[3]}"`);
  }
  // alias.Columna LIKE 'literal' o LIKE "literal"
  const likeSingle = /(?:(\w+)\.)?(\w+|\[\w+\])\s+LIKE\s*'([^']*)'/gi;
  while ((m = likeSingle.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], 'LIKE', `${m[1] ? m[1] + '.' : ''}${m[2]} LIKE '${m[3]}'`);
  }
  const likeDouble = /(?:(\w+)\.)?(\w+|\[\w+\])\s+LIKE\s*"([^"]*)"/gi;
  while ((m = likeDouble.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], 'LIKE', `${m[1] ? m[1] + '.' : ''}${m[2]} LIKE "${m[3]}"`);
  }
  // LTRIM(RTRIM(alias.Columna)) LIKE 'literal' o LIKE "literal"
  const ltrimRtrimLikeSingle = /LTRIM\s*\(\s*RTRIM\s*\(\s*(?:(\w+)\.)?(\w+)\s*\)\s*\)\s+LIKE\s*'([^']*)'/gi;
  while ((m = ltrimRtrimLikeSingle.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], 'LIKE', `LTRIM(RTRIM(${m[1] ? m[1] + '.' : ''}${m[2]})) LIKE '${m[3]}'`);
  }
  const ltrimRtrimLikeDouble = /LTRIM\s*\(\s*RTRIM\s*\(\s*(?:(\w+)\.)?(\w+)\s*\)\s*\)\s+LIKE\s*"([^"]*)"/gi;
  while ((m = ltrimRtrimLikeDouble.exec(trimmed)) !== null) {
    add(m[1] || null, m[2], [m[3]], 'LIKE', `LTRIM(RTRIM(${m[1] ? m[1] + '.' : ''}${m[2]})) LIKE "${m[3]}"`);
  }
  // alias.Columna IN ('a','b','c')
  const inRegex = /(?:(\w+)\.)?(\w+|\[\w+\])\s+IN\s*\(\s*((?:'[^']*'|"[^"]*")(?:\s*,\s*(?:'[^']*'|"[^"]*"))*\s*)\s*\)/gi;
  while ((m = inRegex.exec(trimmed)) !== null) {
    const listStr = m[3];
    const inner = listStr.match(/'[^']*'|"[^"]*"/g) || [];
    const values = inner.map(s => s.slice(1, -1));
    if (values.length > 0) {
      add(m[1] || null, m[2], values, 'IN', `${m[1] ? m[1] + '.' : ''}${m[2]} IN (${values.length} valor${values.length !== 1 ? 'es' : ''})`);
    }
  }
  // Agrupar por canonicalParamName + primer valor: una sola sugerencia por "concepto" (ej. KOPR/KOPRCT = '004...')
  const byKey = new Map();
  for (const c of candidates) {
    const val = c.values && c.values[0];
    const key = `${(c.canonicalParamName || c.columnName)}|${val ?? ''}`;
    if (!byKey.has(key)) {
      byKey.set(key, { ...c, snippets: [c.snippet] });
    } else {
      const existing = byKey.get(key);
      if (!existing.snippets.includes(c.snippet)) existing.snippets.push(c.snippet);
    }
  }
  const baseCandidates = Array.from(byKey.values()).map((c) => {
    const { snippets, ...rest } = c;
    return {
      ...rest,
      snippet: snippets.length > 1 ? `${c.canonicalParamName || c.columnName} = '${c.values?.[0] ?? ''}' (${snippets.length} sitios en la query)` : (c.snippet || ''),
    };
  });

  const dateCandidates = [];
  const seenDate = new Set();
  const dateCmpRegex = /(?:(\w+)\.)?(\w+|\[\w+\])\s*(>=|>|<=|<)\s*([^\n\r]+)/gi;
  const foundByCol = new Map();
  while ((m = dateCmpRegex.exec(trimmed)) !== null) {
    const alias = m[1] || null;
    const col = colNorm(m[2]);
    const rhs = String(m[4] || '');
    const expr = `${alias ? `${alias}.` : ''}${col}`;
    const maybeDate = isDateLikeColumn(col) || /DESDE|HASTA|GETDATE|CURRENT_TIMESTAMP|DATEADD|DATEDIFF|CAST\s*\(|CONVERT\s*\(/i.test(rhs);
    if (!maybeDate) continue;
    const key = `${alias || ''}.${col}`;
    if (!foundByCol.has(key)) foundByCol.set(key, { alias, col, expr });
  }

  foundByCol.forEach((it) => {
    const key = `${it.alias || ''}.${it.col}`;
    const tableName = resolveTable(it.alias);
    if (!seenDate.has(`range:${key}`)) {
      seenDate.add(`range:${key}`);
      dateCandidates.push({
        candidateType: 'date-range',
        columnName: it.col,
        canonicalParamName: 'FechaInicio',
        values: [],
        type: 'DATE_RANGE',
        snippet: `Rango de fechas en ${it.expr} (@FechaInicio/@FechaFin)`,
        dateColumnExpr: it.expr,
        paramConfigs: [
          { enabled: true, paramName: 'FechaInicio', filterType: 'date', allowExecuteWithoutSelection: true, selectedValue: '' },
          { enabled: true, paramName: 'FechaFin', filterType: 'date', allowExecuteWithoutSelection: true, selectedValue: '' },
        ],
      });
    }
    if (!seenDate.has(`year:${key}`)) {
      seenDate.add(`year:${key}`);
      const yearQuery = tableName ? `SELECT DISTINCT YEAR(${it.expr}) AS Anio FROM ${tableName} ORDER BY Anio DESC` : '';
      dateCandidates.push({
        candidateType: 'date-part-year',
        columnName: it.col,
        canonicalParamName: 'Anio',
        values: [],
        type: 'DATE_YEAR',
        snippet: `Año en ${it.expr} (@Anio)`,
        dateColumnExpr: it.expr,
        suggestedOptionsQuery: yearQuery,
        suggestedValueColumn: 'Anio',
        suggestedLabelColumn: 'Anio',
        paramConfigs: [
          { enabled: true, paramName: 'Anio', filterType: 'select', optionSource: 'query', optionsQuery: yearQuery, valueColumn: 'Anio', labelColumn: 'Anio', allowExecuteWithoutSelection: true, selectedValue: '' },
        ],
      });
    }
    if (!seenDate.has(`month:${key}`)) {
      seenDate.add(`month:${key}`);
      dateCandidates.push({
        candidateType: 'date-part-month',
        columnName: it.col,
        canonicalParamName: 'Mes',
        values: [],
        type: 'DATE_MONTH',
        snippet: `Mes en ${it.expr} (@Mes)`,
        dateColumnExpr: it.expr,
        paramConfigs: [
          {
            enabled: true,
            paramName: 'Mes',
            filterType: 'select',
            optionSource: 'static',
            staticOptions: [
              { value: 1, label: '01 - Enero' }, { value: 2, label: '02 - Febrero' }, { value: 3, label: '03 - Marzo' },
              { value: 4, label: '04 - Abril' }, { value: 5, label: '05 - Mayo' }, { value: 6, label: '06 - Junio' },
              { value: 7, label: '07 - Julio' }, { value: 8, label: '08 - Agosto' }, { value: 9, label: '09 - Septiembre' },
              { value: 10, label: '10 - Octubre' }, { value: 11, label: '11 - Noviembre' }, { value: 12, label: '12 - Diciembre' },
            ],
            allowExecuteWithoutSelection: true,
            selectedValue: '',
          },
        ],
      });
    }
  });

  // Reglas declarativas: sugerir filtros aunque no haya literal en WHERE.
  // Usa dynamicFilterOptionSources con:
  // { columnNames: ["FEEMDO"], kind: "date", forceSuggestWhenPresent: true, suggestions: ["date_range","year","month"] }
  sources.forEach((rule) => {
    if (!rule || rule.forceSuggestWhenPresent !== true || !Array.isArray(rule.columnNames) || rule.columnNames.length === 0) return;
    const kind = String(rule.kind || '').toLowerCase();
    const isDateRule = kind === 'date';
    const suggestions = Array.isArray(rule.suggestions) && rule.suggestions.length > 0
      ? rule.suggestions.map((s) => String(s).toLowerCase())
      : (isDateRule ? ['date_range', 'year', 'month'] : []);
    if (!isDateRule && suggestions.length === 0) return;

    let matchedCol = null;
    let matchedAlias = null;
    for (const rawCol of rule.columnNames) {
      const col = String(rawCol || '').trim();
      if (!col) continue;
      const colRegex = new RegExp(`(?:(\\w+)\\.)?\\b${escReg(col.replace(/^\[|\]$/g, ''))}\\b`, 'i');
      const mCol = trimmed.match(colRegex);
      if (mCol) {
        matchedCol = col;
        matchedAlias = mCol[1] || null;
        break;
      }
    }
    if (!matchedCol) return; // ninguna columna de la regla aparece en la query

    const alias = matchedAlias;
    const expr = `${alias ? `${alias}.` : ''}${matchedCol.replace(/^\[|\]$/g, '')}`;
    const key = `${alias || ''}.${matchedCol}`;
    const tableName = resolveTable(alias);

    if (suggestions.includes('date_range') && !seenDate.has(`range:${key}`)) {
      seenDate.add(`range:${key}`);
      dateCandidates.push({
        candidateType: 'date-range',
        columnName: matchedCol,
        canonicalParamName: 'FechaInicio',
        values: [],
        type: 'DATE_RANGE',
        snippet: `Rango de fechas en ${expr} (@FechaInicio/@FechaFin)`,
        dateColumnExpr: expr,
        paramConfigs: [
          { enabled: true, paramName: 'FechaInicio', filterType: 'date', allowExecuteWithoutSelection: true, selectedValue: '' },
          { enabled: true, paramName: 'FechaFin', filterType: 'date', allowExecuteWithoutSelection: true, selectedValue: '' },
        ],
      });
    }
    if (suggestions.includes('year') && !seenDate.has(`year:${key}`)) {
      seenDate.add(`year:${key}`);
      const yearQuery = tableName ? `SELECT DISTINCT YEAR(${expr}) AS Anio FROM ${tableName} ORDER BY Anio DESC` : '';
      dateCandidates.push({
        candidateType: 'date-part-year',
        columnName: matchedCol,
        canonicalParamName: 'Anio',
        values: [],
        type: 'DATE_YEAR',
        snippet: `Año en ${expr} (@Anio)`,
        dateColumnExpr: expr,
        suggestedOptionsQuery: yearQuery,
        suggestedValueColumn: 'Anio',
        suggestedLabelColumn: 'Anio',
        paramConfigs: [
          { enabled: true, paramName: 'Anio', filterType: 'select', optionSource: 'query', optionsQuery: yearQuery, valueColumn: 'Anio', labelColumn: 'Anio', allowExecuteWithoutSelection: true, selectedValue: '' },
        ],
      });
    }
    if (suggestions.includes('month') && !seenDate.has(`month:${key}`)) {
      seenDate.add(`month:${key}`);
      dateCandidates.push({
        candidateType: 'date-part-month',
        columnName: matchedCol,
        canonicalParamName: 'Mes',
        values: [],
        type: 'DATE_MONTH',
        snippet: `Mes en ${expr} (@Mes)`,
        dateColumnExpr: expr,
        paramConfigs: [
          {
            enabled: true,
            paramName: 'Mes',
            filterType: 'select',
            optionSource: 'static',
            staticOptions: [
              { value: 1, label: '01 - Enero' }, { value: 2, label: '02 - Febrero' }, { value: 3, label: '03 - Marzo' },
              { value: 4, label: '04 - Abril' }, { value: 5, label: '05 - Mayo' }, { value: 6, label: '06 - Junio' },
              { value: 7, label: '07 - Julio' }, { value: 8, label: '08 - Agosto' }, { value: 9, label: '09 - Septiembre' },
              { value: 10, label: '10 - Octubre' }, { value: 11, label: '11 - Noviembre' }, { value: 12, label: '12 - Diciembre' },
            ],
            allowExecuteWithoutSelection: true,
            selectedValue: '',
          },
        ],
      });
    }
  });

  return [...dateCandidates, ...baseCandidates];
}

const ChartEditDialog = ({ 
  open, 
  onClose, 
  onSave, 
  chart, 
  availableColumns = [],
  queryData = null,
  mode = 'edit', // 'edit' o 'preview'
  initialValues = null, // Para modo preview: { type, xColumn, yColumn, groupByColumn, labelColumn, stacked, advancedConfig }
  editingChart = null, // Gráfico que se está editando (modo preview): muestra "Editando gráfico existente"
  embedded = false, // Si true, renderiza solo el contenido (config + preview) sin Dialog, para usar dentro del preview del editor SQL
  value = null, // Cuando embedded: config actual { type, xColumn, yColumn, groupByColumn, labelColumn, stacked, advancedConfig }
  onChange = null, // Cuando embedded: (config) => void
  currentQuery = null, // Query SQL actual (para sugerir filtros dinámicos desde comparaciones con literales)
  onReplaceQueryLiteral = null, // (paramName, literalValue) => void — reemplaza en la query el literal por @paramName (llamado al elegir una sugerencia)
  dynamicFilterOptionSources = [], // Reglas desde configuración admin: [{ columnNames, optionsQuery, valueColumn, labelColumn }]
  queryDynamicFilterConfig = null, // Cuando embedded: config del filtro dinámico a nivel de query (solo se muestra enable/disable y param aquí)
  getAuthHeaders = null, // Para MapWidget: caché de geocodificación en appdata
  currentDatabase = '',
}) => {
  const dynamicFilterOptionsQueryMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/query', body, { headers }),
  });
  const [tabValue, setTabValue] = useState(0);
  const [chartTypeState, setChartTypeState] = useState('line');
  const [xColumnState, setXColumnState] = useState('');
  const [yColumnState, setYColumnState] = useState('');
  const [groupByColumnState, setGroupByColumnState] = useState('');
  const [labelColumnState, setLabelColumnState] = useState('');
  const [stackedState, setStackedState] = useState(false);
  const defaultAdvancedConfig = {
    title: '',
    xAxisTitle: '',
    yAxisTitle: '',
    legendPosition: 'top',
    legendDisplay: true,
    gridLines: true,
    xAxisMin: '',
    xAxisMax: '',
    yAxisMin: '',
    yAxisMax: '',
    pointRadius: '',
    borderWidth: '',
    seriesColors: {},
    seriesColorIndex: {},
    tableShowTotalColumn: false,
    tableShowTotalRow: false,
    tableVisibleColumns: null, // null = todas; string[] = solo estas
    tableCalculatedColumns: [], // [{ id, label, left, op, right }] calculadas en el cliente, por fila
    tableColumnDecimals: {}, // { [columnName]: number } decimales a mostrar (solo numéricas)
    tableColumnDateFormats: {}, // { [columnName]: string } formato fecha/hora por columna
    tableColumnFilters: {}, // { [columnName]: string[] } valores permitidos por columna (vacío = sin filtro)
    barShowTotalLabel: false,
    barShowSegmentLabel: false,
    lineShowValueLabel: false,
    lineFillMissingAsZero: true,
    fillTimeAxisGaps: false,
    lineInterpolation: 'linear',
    // Etiquetas en pastel/dona
    pieShowValueLabel: true,
    pieShowPercentLabel: true,
    pieShowNameLabel: false,
    pieLabelPosition: DEFAULT_PIE_LABEL_POSITION,
    pieLegendFontSize: 'lg',
    dynamicFilter: { ...DEFAULT_DYNAMIC_FILTER },
    dynamicFilters: [],
  };
  const [advancedConfigState, setAdvancedConfigState] = useState(defaultAdvancedConfig);
  const [heatmapGridConfigState, setHeatmapGridConfigState] = useState(DEFAULT_HEATMAP_GRID_CONFIG);
  const [mapConfigState, setMapConfigState] = useState(DEFAULT_MAP_CONFIG);
  const heatmapImageInputRef = useRef(null);

  // Cuando embedded, los valores efectivos vienen de value/onChange; si no, del estado local
  const chartType = embedded ? (value?.type ?? 'line') : chartTypeState;
  const setChartType = embedded ? (v) => onChange?.({ ...value, type: v }) : setChartTypeState;
  const xColumn = embedded ? (value?.xColumn ?? '') : xColumnState;
  const setXColumn = embedded ? (v) => onChange?.({ ...value, xColumn: v }) : setXColumnState;
  const yColumn = embedded ? (value?.yColumn ?? '') : yColumnState;
  const setYColumn = embedded ? (v) => onChange?.({ ...value, yColumn: v }) : setYColumnState;
  const groupByColumn = embedded ? (value?.groupByColumn ?? '') : groupByColumnState;
  const setGroupByColumn = embedded ? (v) => onChange?.({ ...value, groupByColumn: v }) : setGroupByColumnState;
  const labelColumn = embedded ? (value?.labelColumn ?? '') : labelColumnState;
  const setLabelColumn = embedded ? (v) => onChange?.({ ...value, labelColumn: v }) : setLabelColumnState;
  const stacked = embedded ? (value?.stacked ?? false) : stackedState;
  const setStacked = embedded ? (v) => onChange?.({ ...value, stacked: v }) : setStackedState;
  const advancedConfig = embedded ? (value?.advancedConfig ?? defaultAdvancedConfig) : advancedConfigState;
  const setAdvancedConfig = embedded
    ? (patchOrFn) => {
        const next = typeof patchOrFn === 'function' ? patchOrFn(value?.advancedConfig ?? defaultAdvancedConfig) : { ...(value?.advancedConfig ?? defaultAdvancedConfig), ...patchOrFn };
        onChange?.({ ...value, advancedConfig: next });
      }
    : setAdvancedConfigState;
  const heatmapGridConfig = embedded ? (value?.heatmapGridConfig ?? DEFAULT_HEATMAP_GRID_CONFIG) : heatmapGridConfigState;
  const setHeatmapGridConfig = embedded
    ? (patchOrFn) => {
        const prev = value?.heatmapGridConfig ?? DEFAULT_HEATMAP_GRID_CONFIG;
        const next = typeof patchOrFn === 'function' ? patchOrFn(prev) : { ...prev, ...patchOrFn };
        onChange?.({ ...value, heatmapGridConfig: next });
      }
    : (patchOrFn) => setHeatmapGridConfigState((prev) => (typeof patchOrFn === 'function' ? patchOrFn(prev) : { ...prev, ...patchOrFn }));
  const mapConfig = embedded ? (value?.mapConfig ?? DEFAULT_MAP_CONFIG) : mapConfigState;
  const setMapConfig = embedded
    ? (patchOrFn) => {
        const prev = value?.mapConfig ?? DEFAULT_MAP_CONFIG;
        const next = typeof patchOrFn === 'function' ? patchOrFn(prev) : { ...prev, ...patchOrFn };
        onChange?.({ ...value, mapConfig: next });
      }
    : (patchOrFn) => setMapConfigState((prev) => (typeof patchOrFn === 'function' ? patchOrFn(prev) : { ...prev, ...patchOrFn }));

  const chartForDateBucket = embedded ? value : chart || editingChart;
  const chartSupportsFillTimeAxisGaps = useMemo(() => {
    if (chartType !== 'line' && chartType !== 'bar') return false;
    const bucket = resolveXDateBucket(chartForDateBucket);
    if (bucket) return supportsFillTimeAxisGaps(bucket);
    return Boolean(chartForDateBucket?.visualizationSpec?.dimension?.dateBucket);
  }, [chartType, chartForDateBucket]);

  const dynamicFilters = useMemo(
    () => normalizeDynamicFiltersFromConfig(advancedConfig),
    [advancedConfig]
  );
  const [dynamicFilterPreviewOptions, setDynamicFilterPreviewOptions] = useState({});
  const [dynamicFilterPreviewLoading, setDynamicFilterPreviewLoading] = useState({});
  const setDynamicFilters = (nextFilters) => {
    const safe = Array.isArray(nextFilters) ? nextFilters : [];
    setAdvancedConfig((prev) => ({
      ...prev,
      dynamicFilters: safe,
      dynamicFilter: safe[0] || { ...DEFAULT_DYNAMIC_FILTER },
    }));
  };

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      const nextOptions = {};
      const nextLoading = {};
      const updates = [];
      const trimIfString = (v) => (v != null && typeof v === 'string' ? v.trim() : v);

      for (let idx = 0; idx < dynamicFilters.length; idx += 1) {
        const df = dynamicFilters[idx];
        const paramName = String(df?.paramName || '').trim();
        const key = `${idx}::${paramName}`;
        if (!df?.enabled || !paramName) {
          nextOptions[key] = [];
          continue;
        }

        if (df.filterType === 'date') {
          nextOptions[key] = [];
          const selected = df.selectedValue;
          if (selected == null || String(selected).trim() === '') {
            updates.push({ idx, value: new Date().toISOString().slice(0, 10) });
          }
          continue;
        }

        const hasStatic = df.optionSource === 'static' && Array.isArray(df.staticOptions) && df.staticOptions.length > 0;
        const hasQuery = (df.optionsQuery || '').trim() && df.valueColumn && df.labelColumn;

        if (hasStatic && !hasQuery) {
          const options = df.staticOptions.map((o, i) => ({
            label: String(o.label ?? o.value ?? '').trim(),
            value: trimIfString(o.value),
            _key: `preview-static-${key}-${i}`,
          }));
          nextOptions[key] = options;
          const selected = df.selectedValue;
          if ((selected == null || String(selected).trim() === '') && options[0]?.value !== undefined) {
            updates.push({ idx, value: options[0].value });
          }
          continue;
        }

        if (!hasQuery || !currentDatabase) {
          nextOptions[key] = [];
          continue;
        }

        nextLoading[key] = true;
        try {
          const q = (df.optionsQuery || '').trim();
          const fullQuery = /^\s*USE\s+/i.test(q) ? q : `USE [${currentDatabase}];\n${q}`;
          const res = await dynamicFilterOptionsQueryMutation.mutateAsync({
            body: { query: fullQuery, database: currentDatabase },
            headers: getAuthHeaders?.() || {},
          });
          const rows = Array.isArray(res.data?.data) ? res.data.data : [];
          if (rows.length === 0) {
            nextOptions[key] = [];
            continue;
          }
          const row0 = rows[0];
          const colNames = Array.isArray(res.data?.columns) && res.data.columns.length > 0 ? res.data.columns : Object.keys(row0 || {});
          const findCol = (name) => {
            if (!name || !row0) return null;
            if (Object.prototype.hasOwnProperty.call(row0, name)) return name;
            const lower = String(name).toLowerCase();
            return colNames.find((c) => String(c).toLowerCase() === lower) || colNames[0] || null;
          };
          const labelCol = findCol(df.labelColumn || paramName);
          const valueCol = findCol(df.valueColumn || paramName);
          const options = rows.map((row, i) => {
            const label = labelCol ? String(row[labelCol] ?? '').trim() : '';
            const value = valueCol != null ? trimIfString(row[valueCol]) : '';
            return { label: label || value, value: value || label, _key: `preview-query-${key}-${i}` };
          });
          nextOptions[key] = options;
          const selected = df.selectedValue;
          if ((selected == null || String(selected).trim() === '') && options[0]?.value !== undefined) {
            updates.push({ idx, value: options[0].value });
          }
        } catch {
          nextOptions[key] = [];
        } finally {
          nextLoading[key] = false;
        }
      }

      if (cancelled) return;
      setDynamicFilterPreviewOptions(nextOptions);
      setDynamicFilterPreviewLoading(nextLoading);
      if (updates.length > 0) {
        setDynamicFilters(dynamicFilters.map((f, i) => {
          const upd = updates.find((u) => u.idx === i);
          return upd ? { ...f, selectedValue: upd.value } : f;
        }));
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, [dynamicFilters, currentDatabase, getAuthHeaders]);

  const { palette } = useStyleTemplate();
  const pal = useMemo(() => {
    const fallback = vsaasTheme.chart;
    const p = palette?.colors?.length ? palette : fallback;
    const colors = p.colors || fallback.colors;
    const colorsWithAlpha = p.colorsWithAlpha?.length ? p.colorsWithAlpha : colors.map(() => 'rgba(94,110,180,0.6)');
    const borderColors = p.borderColors?.length ? p.borderColors : [...colors];
    return { colors, colorsWithAlpha, borderColors };
  }, [palette]);

  // Inicializar valores desde el gráfico existente o valores iniciales (modo preview). No correr cuando embedded.
  useEffect(() => {
    if (!embedded && open) {
      if (mode === 'preview' && initialValues) {
        setChartTypeState(initialValues.type || 'line');
        setXColumnState(initialValues.xColumn || '');
        setYColumnState(initialValues.yColumn || '');
        setGroupByColumnState(initialValues.groupByColumn || '');
        setLabelColumnState(initialValues.labelColumn || '');
        setStackedState(initialValues.stacked || false);
        setMapConfigState({ ...DEFAULT_MAP_CONFIG, ...(initialValues.mapConfig || {}) });
        setAdvancedConfigState({
          ...(initialValues.advancedConfig || {}),
          title: initialValues.advancedConfig?.title ?? '',
          xAxisTitle: initialValues.advancedConfig?.xAxisTitle ?? '',
          yAxisTitle: initialValues.advancedConfig?.yAxisTitle ?? '',
          legendPosition: initialValues.advancedConfig?.legendPosition ?? 'top',
          legendDisplay: initialValues.advancedConfig?.legendDisplay !== false,
          gridLines: initialValues.advancedConfig?.gridLines !== false,
          xAxisMin: initialValues.advancedConfig?.xAxisMin ?? '',
          xAxisMax: initialValues.advancedConfig?.xAxisMax ?? '',
          yAxisMin: initialValues.advancedConfig?.yAxisMin ?? '',
          yAxisMax: initialValues.advancedConfig?.yAxisMax ?? '',
          pointRadius: initialValues.advancedConfig?.pointRadius ?? '',
          borderWidth: initialValues.advancedConfig?.borderWidth ?? '',
          seriesColors: initialValues.advancedConfig?.seriesColors ?? {},
          seriesColorIndex: initialValues.advancedConfig?.seriesColorIndex ?? {},
          dynamicFilters: normalizeDynamicFiltersFromConfig(initialValues.advancedConfig),
          dynamicFilter: normalizeDynamicFiltersFromConfig(initialValues.advancedConfig)[0] ?? { ...DEFAULT_DYNAMIC_FILTER },
        });
      } else if (mode === 'edit' && chart) {
        // Modo edit: usar gráfico existente
        setChartTypeState(chart.type || 'line');
        setXColumnState(chart.xColumn || '');
        setYColumnState(chart.yColumn || '');
        setGroupByColumnState(chart.groupByColumn || '');
        setLabelColumnState(chart.labelColumn || '');
        setStackedState(chart.stacked || false);
        setHeatmapGridConfigState({
          ...DEFAULT_HEATMAP_GRID_CONFIG,
          ...(chart.heatmapGridConfig || {}),
        });
        setMapConfigState({
          ...DEFAULT_MAP_CONFIG,
          ...(chart.mapConfig || {}),
        });
        setAdvancedConfigState({
          ...(chart.advancedConfig || {}),
          title: chart.advancedConfig?.title !== undefined ? chart.advancedConfig.title : (chart.label || ''),
          xAxisTitle: chart.advancedConfig?.xAxisTitle ?? '',
          yAxisTitle: chart.advancedConfig?.yAxisTitle ?? '',
          legendPosition: chart.advancedConfig?.legendPosition ?? 'top',
          legendDisplay: chart.advancedConfig?.legendDisplay !== false,
          gridLines: chart.advancedConfig?.gridLines !== false,
          xAxisMin: chart.advancedConfig?.xAxisMin ?? '',
          xAxisMax: chart.advancedConfig?.xAxisMax ?? '',
          yAxisMin: chart.advancedConfig?.yAxisMin ?? '',
          yAxisMax: chart.advancedConfig?.yAxisMax ?? '',
          pointRadius: chart.advancedConfig?.pointRadius ?? '',
          borderWidth: chart.advancedConfig?.borderWidth ?? '',
          seriesColors: chart.advancedConfig?.seriesColors ?? {},
          seriesColorIndex: chart.advancedConfig?.seriesColorIndex ?? {},
          dynamicFilters: normalizeDynamicFiltersFromConfig(chart.advancedConfig),
          dynamicFilter: normalizeDynamicFiltersFromConfig(chart.advancedConfig)[0] ?? { ...DEFAULT_DYNAMIC_FILTER },
        });
      }
    }
  }, [embedded, open, chart, mode, initialValues]);

  // Obtener columnas disponibles de los datos
  const getAvailableColumns = () => {
    // Primero intentar usar las columnas del queryData si están disponibles
    if (queryData?.columns && Array.isArray(queryData.columns) && queryData.columns.length > 0) {
      return queryData.columns;
    }
    
    // Si no hay columnas explícitas, obtenerlas de los datos
    if (queryData?.data && Array.isArray(queryData.data) && queryData.data.length > 0) {
      const firstRow = queryData.data[0];
      if (firstRow && typeof firstRow === 'object') {
        return Object.keys(firstRow);
      }
    }
    
    // Fallback a availableColumns si se proporcionan
    return availableColumns.length > 0 ? availableColumns : [];
  };

  const columns = getAvailableColumns();

  const handleSave = () => {
    const chartData = {
      type: chartType,
      xColumn: (chartType === 'table' || chartType === 'heatmapGrid' || chartType === 'map') ? undefined : xColumn,
      yColumn: (chartType === 'table' || chartType === 'heatmapGrid' || chartType === 'map') ? undefined : yColumn,
      groupByColumn: groupByColumn || undefined,
      labelColumn: labelColumn || undefined,
      stacked: chartType === 'bar' && (groupByColumn || labelColumn) ? stacked : undefined,
      advancedConfig: advancedConfig,
      heatmapGridConfig: chartType === 'heatmapGrid' ? heatmapGridConfig : undefined,
      mapConfig: chartType === 'map' ? mapConfig : undefined,
    };

    if (mode === 'edit' && chart) {
      // Modo edit: actualizar gráfico existente
      const updatedChart = {
        ...chart,
        ...chartData,
      };
      if (chartType === 'imageCarousel' && advancedConfig?.imageCarouselConfig) {
        const carouselViz = imageCarouselVizFieldsFromConfig(advancedConfig.imageCarouselConfig);
        const prevSpec = chart.visualizationSpec && typeof chart.visualizationSpec === 'object'
          ? chart.visualizationSpec
          : {};
        updatedChart.visualizationSpec = {
          ...prevSpec,
          visualization: {
            ...(prevSpec.visualization || {}),
            chartType: 'imageCarousel',
            ...carouselViz,
          },
        };
        updatedChart.imageCarouselConfig = advancedConfig.imageCarouselConfig;
      }
      onSave(updatedChart);
    } else if (mode === 'preview') {
      // Modo preview: pasar solo los datos del gráfico
      onSave(chartData);
    }
    onClose();
  };

  const handleAdvancedConfigChange = (field, value) => {
    setAdvancedConfig(prev => ({ ...prev, [field]: value }));
  };

  const isPieChart = chartType === 'pie' || chartType === 'doughnut';
  const isHeatmapGrid = chartType === 'heatmapGrid';
  const isMap = chartType === 'map';

  // Obtener datos para la previsualización
  const previewData = useMemo(() => {
    if (!queryData?.data || !Array.isArray(queryData.data) || queryData.data.length === 0) {
      return [];
    }
    const source = value || chart;
    return applyCommittedHeatmapPositionColumns(queryData.data, source);
  }, [queryData, value, chart]);

  // Verificar si hay datos suficientes para previsualizar
  const mapHasConfig = chartType === 'map' && previewData.length > 0 && ((mapConfig.latColumn && mapConfig.lngColumn) || mapConfig.addressColumn);
  const canPreview = chartType === 'table' || chartType === 'heatmapGrid' || chartType === 'imageCarousel' || mapHasConfig || (xColumn && yColumn && previewData.length > 0);

  // Etiquetas de leyenda (series) para la pestaña Estilo
  const legendLabels = useMemo(() => {
    if (!previewData?.length || !xColumn || !yColumn || chartType === 'table') return [];
    const data = previewData;
    const cell = (row, col) => {
      if (!row || col == null || col === '') return undefined;
      if (Object.prototype.hasOwnProperty.call(row, col)) return row[col];
      const lower = String(col).toLowerCase();
      const hit = Object.keys(row).find((k) => k.toLowerCase() === lower);
      return hit ? row[hit] : undefined;
    };
    if (chartType === 'pie' || chartType === 'doughnut') {
      return [...new Set(data.map((r) => String(cell(r, xColumn) ?? '')))].filter(Boolean).sort();
    }
    if (chartType === 'scatter' && labelColumn) {
      return [...new Set(data.map((r) => String(cell(r, labelColumn) ?? 'Sin etiqueta')))].sort();
    }
    if (chartType === 'line' || chartType === 'bar') {
      if (labelColumn) return [...new Set(data.map((r) => String(cell(r, labelColumn) ?? 'Sin etiqueta')))].sort();
      if (groupByColumn) return [...new Set(data.map((r) => String(cell(r, groupByColumn) ?? 'Sin grupo')))].sort();
      return [yColumn];
    }
    return [];
  }, [previewData, chartType, xColumn, yColumn, labelColumn, groupByColumn]);

  const handleSeriesColorIndexChange = (label, paletteIndex) => {
    setAdvancedConfig((prev) => ({
      ...prev,
      seriesColorIndex: { ...(prev.seriesColorIndex || {}), [label]: paletteIndex },
    }));
  };
  const isHeatmapChart = chartType === 'heatmapGrid';

  const previewAxesSeriesPanel = (
    <ChartPreviewAxesSeriesPanel
      chartType={chartType}
      columns={columns}
      xColumn={xColumn}
      yColumn={yColumn}
      groupByColumn={groupByColumn}
      labelColumn={labelColumn}
      stacked={stacked}
      onXColumnChange={setXColumn}
      onYColumnChange={setYColumn}
      onGroupByColumnChange={setGroupByColumn}
      onLabelColumnChange={setLabelColumn}
      onStackedChange={setStacked}
      advancedConfig={advancedConfig}
      onAdvancedConfigChange={handleAdvancedConfigChange}
      legendLabels={legendLabels}
      palette={pal}
      onSeriesColorIndexChange={handleSeriesColorIndexChange}
      heatmapGridConfig={heatmapGridConfig}
      onHeatmapGridConfigChange={setHeatmapGridConfig}
      mapConfig={mapConfig}
      onMapConfigChange={setMapConfig}
    />
  );

  const innerContent = (
          <Box sx={{ display: 'flex', flex: 1, height: embedded ? '100%' : '70vh', minHeight: 0, flexDirection: 'column', flexWrap: 'nowrap' }}>
            {/* Panel configuración: arriba (ancho completo) */}
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0, maxHeight: embedded ? '42%' : '48%' }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                  <Tab label="Datos del Gráfico" />
                  <Tab label="Propiedades del Gráfico" />
                </Tabs>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {tabValue === 0 && (
                  <Box>
              <Grid container spacing={2}>
                {/* Tipo de gráfico */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Gráfico</InputLabel>
                    <Select
                      value={chartType}
                      label="Tipo de Gráfico"
                      onChange={(e) => {
                        const newType = e.target.value;
                        if (newType === 'table' || newType === 'heatmapGrid' || newType === 'map') {
                          if (embedded) {
                            onChange({ ...value, type: newType, xColumn: '', yColumn: '', groupByColumn: '', labelColumn: '', ...(newType === 'map' ? { mapConfig: DEFAULT_MAP_CONFIG } : {}) });
                          } else {
                            setChartType(newType);
                            setXColumn('');
                            setYColumn('');
                            setGroupByColumn('');
                            setLabelColumn('');
                            if (newType === 'map') setMapConfigState(DEFAULT_MAP_CONFIG);
                          }
                        } else {
                          setChartType(newType);
                        }
                      }}
                    >
                      {CHART_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {chartType === 'table' && columns.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Columnas visibles
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Elige qué columnas mostrar en la tabla. Si no desmarcas ninguna, se muestran todas.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {columns.map((col) => {
                          const visible = advancedConfig.tableVisibleColumns == null ||
                            (Array.isArray(advancedConfig.tableVisibleColumns) && advancedConfig.tableVisibleColumns.includes(col));
                          const selectedCount = advancedConfig.tableVisibleColumns == null ? columns.length : advancedConfig.tableVisibleColumns.length;
                          const isLastVisible = visible && selectedCount === 1;
                          return (
                            <FormControlLabel
                              key={col}
                              control={
                                <Checkbox
                                  checked={visible}
                                  disabled={isLastVisible}
                                  onChange={(e) => {
                                    const list = advancedConfig.tableVisibleColumns == null ? [...columns] : advancedConfig.tableVisibleColumns;
                                    const next = e.target.checked
                                      ? (list.includes(col) ? list : [...list, col])
                                      : list.filter((c) => c !== col);
                                    setAdvancedConfig((prev) => ({ ...prev, tableVisibleColumns: next.length === columns.length ? null : next }));
                                  }}
                                />
                              }
                              label={col}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Decimales por columna
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Opcional: número de decimales para columnas numéricas (vacío = sin límite).
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        {columns.map((col) => (
                          <Box key={col} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 80 }} noWrap title={col}>
                              {col.length > 12 ? col.slice(0, 12) + '…' : col}
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              placeholder="—"
                              value={advancedConfig.tableColumnDecimals?.[col] ?? ''}
                              onChange={(e) => {
                                const v = e.target.value.trim();
                                const num = v === '' ? undefined : Math.min(10, Math.max(0, parseInt(v, 10) || 0));
                                const nextDecimals = { ...(advancedConfig.tableColumnDecimals || {}) };
                                if (num === undefined) delete nextDecimals[col]; else nextDecimals[col] = num;
                                setAdvancedConfig((prev) => ({ ...prev, tableColumnDecimals: nextDecimals }));
                              }}
                              inputProps={{ min: 0, max: 10, step: 1 }}
                              sx={{ width: 56 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Formato de fecha/hora por columna
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Solo visualización. Automático detecta columnas de tiempo y muestra fecha y hora completas.
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        {columns
                          .filter((col) =>
                            isLikelyDateColumn(
                              col,
                              previewData?.[0]?.[col],
                              advancedConfig.tableColumnDateFormats
                            )
                          )
                          .map((col) => (
                            <Box key={`datefmt-${col}`} sx={{ mb: 1.5 }}>
                              <Typography variant="body2" sx={{ mb: 0.5 }} noWrap title={col}>
                                {col}
                              </Typography>
                              <DateDisplayFormatControl
                                format={advancedConfig.tableColumnDateFormats?.[col]}
                                previewValue={previewData?.[0]?.[col]}
                                onChange={(fmt) => {
                                  const next = { ...(advancedConfig.tableColumnDateFormats || {}) };
                                  if (!fmt || fmt === 'auto') delete next[col];
                                  else next[col] = fmt;
                                  setAdvancedConfig((prev) => ({ ...prev, tableColumnDateFormats: next }));
                                }}
                                selectLabel="Formato"
                                showHelper={false}
                              />
                            </Box>
                          ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <CalculatedColumnsEditor
                        columns={columns}
                        value={advancedConfig.tableCalculatedColumns || []}
                        onChange={(next) => setAdvancedConfig((prev) => ({ ...prev, tableCalculatedColumns: next }))}
                      />
                    </Grid>
                  </>
                )}

                {chartType === 'heatmapGrid' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Columna de valor, filtro y tamaño de grilla: panel <strong>Ejes y series</strong> junto a la vista previa. Aquí configura imagen y opciones de visualización; en la preview haz clic en cada celda para asignar el valor del filtro.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Valor secundario (opcional)</InputLabel>
                        <Select
                          value={(heatmapGridConfig.secondaryValueColumn || '') === '' || columns.includes(heatmapGridConfig.secondaryValueColumn || '') ? (heatmapGridConfig.secondaryValueColumn || '') : ''}
                          label="Valor secundario (opcional)"
                          onChange={(e) => setHeatmapGridConfig({ secondaryValueColumn: e.target.value })}
                        >
                          <MenuItem value=""><em>Ninguna</em></MenuItem>
                          {columns.map((col) => (
                            <MenuItem key={col} value={col}>{col}</MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Columna numérica informativa por celda (no afecta el color)
                        </Typography>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={heatmapGridConfig.showValues !== false}
                            onChange={(e) => setHeatmapGridConfig({ showValues: e.target.checked })}
                          />
                        }
                        label="Mostrar valor en cada celda"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={heatmapGridConfig.showFilterValueName === true}
                            onChange={(e) => setHeatmapGridConfig({ showFilterValueName: e.target.checked })}
                          />
                        }
                        label="Mostrar nombre del valor del filtro"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Unidad (opcional)"
                        placeholder="hrs, %, etc."
                        value={heatmapGridConfig.valueUnit ?? ''}
                        onChange={(e) => setHeatmapGridConfig({ valueUnit: e.target.value.slice(0, 5) })}
                        fullWidth
                        size="small"
                        inputProps={{ maxLength: 5 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Imagen de fondo
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<UploadFileIcon />}
                          onClick={() => heatmapImageInputRef.current?.click()}
                        >
                          Subir imagen
                        </Button>
                        <input
                          ref={heatmapImageInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file || !file.type.startsWith('image/')) return;
                            const reader = new FileReader();
                            reader.onload = () => setHeatmapGridConfig({ backgroundImageUrl: reader.result });
                            reader.readAsDataURL(file);
                            e.target.value = '';
                          }}
                        />
                        {heatmapGridConfig.backgroundImageUrl && (
                          <>
                            <Button
                              size="small"
                              color="inherit"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setHeatmapGridConfig({ backgroundImageUrl: '' })}
                            >
                              Quitar imagen
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                              Imagen subida correctamente
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Solo se permiten imágenes subidas desde tu equipo. La grilla se dibuja sobre la imagen.
                      </Typography>
                    </Grid>
                  </>
                )}

                {chartType === 'map' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Latitud, longitud o dirección: panel <strong>Ejes y series</strong> junto a la vista previa. Aquí configura popup, valor del marcador y apariencia.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Columna comuna (opcional)</InputLabel>
                        <Select
                          value={mapConfig.comunaColumn || ''}
                          label="Columna comuna (opcional)"
                          onChange={(e) => setMapConfig({ comunaColumn: e.target.value })}
                        >
                          <MenuItem value=""><em>Ninguna</em></MenuItem>
                          {columns.map((col) => (
                            <MenuItem key={col} value={col}>{col}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Columna región (opcional)</InputLabel>
                        <Select
                          value={mapConfig.regionColumn || ''}
                          label="Columna región (opcional)"
                          onChange={(e) => setMapConfig({ regionColumn: e.target.value })}
                        >
                          <MenuItem value=""><em>Ninguna</em></MenuItem>
                          {columns.map((col) => (
                            <MenuItem key={col} value={col}>{col}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Columna país (opcional)</InputLabel>
                        <Select
                          value={mapConfig.countryColumn || ''}
                          label="Columna país (opcional)"
                          onChange={(e) => setMapConfig({ countryColumn: e.target.value })}
                        >
                          <MenuItem value=""><em>Ninguna</em></MenuItem>
                          {columns.map((col) => (
                            <MenuItem key={col} value={col}>{col}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Contenido del popup</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Elige columnas y orden: se muestran como chips. Flechas para reordenar, X para quitar. Si no eliges ninguna, se usa etiqueta/valor por defecto.
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          alignItems: 'center',
                          minHeight: 40,
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        {(mapConfig.popupColumns || []).map((col, index) => (
                          <Box key={col} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
                            <Chip
                              label={col}
                              size="small"
                              onDelete={() => {
                                const next = (mapConfig.popupColumns || []).filter((c) => c !== col);
                                setMapConfig({ popupColumns: next });
                              }}
                              sx={{ flexShrink: 0 }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', ml: -0.5 }}>
                              <IconButton
                                size="small"
                                sx={{ p: 0.25 }}
                                disabled={index === 0}
                                onClick={() => {
                                  const list = Array.from(mapConfig.popupColumns || []);
                                  if (index <= 0) return;
                                  [list[index - 1], list[index]] = [list[index], list[index - 1]];
                                  setMapConfig({ popupColumns: list });
                                }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ p: 0.25 }}
                                disabled={index === (mapConfig.popupColumns || []).length - 1}
                                onClick={() => {
                                  const list = Array.from(mapConfig.popupColumns || []);
                                  if (index >= list.length - 1) return;
                                  [list[index], list[index + 1]] = [list[index + 1], list[index]];
                                  setMapConfig({ popupColumns: list });
                                }}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      <Autocomplete
                        size="small"
                        options={columns.filter((c) => !(mapConfig.popupColumns || []).includes(c))}
                        value={null}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setMapConfig({ popupColumns: [...(mapConfig.popupColumns || []), newValue] });
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Agregar columna al popup" variant="outlined" sx={{ mt: 1 }} />
                        )}
                        sx={{ maxWidth: 320 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Valor (columna numérica, opcional)</InputLabel>
                        <Select
                          value={mapConfig.valueColumn || ''}
                          label="Valor (columna numérica, opcional)"
                          onChange={(e) => setMapConfig({ valueColumn: e.target.value })}
                        >
                          <MenuItem value=""><em>Ninguna</em></MenuItem>
                          {columns.map((col) => (
                            <MenuItem key={col} value={col}>{col}</MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Se puede usar para tamaño, color o ambos (según opción siguiente)
                        </Typography>
                      </FormControl>
                    </Grid>
                    {mapConfig.valueColumn && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Usar valor para</InputLabel>
                            <Select
                              value={mapConfig.valueMapping || 'size'}
                              label="Usar valor para"
                              onChange={(e) => setMapConfig({ valueMapping: e.target.value })}
                            >
                              {MAP_VALUE_MAPPING_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {(mapConfig.valueMapping === 'color' || mapConfig.valueMapping === 'both') && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Color valor bajo (mínimo)</InputLabel>
                                <Select
                                  value={mapConfig.valueColorMin || '#2196f3'}
                                  label="Color valor bajo (mínimo)"
                                  onChange={(e) => setMapConfig({ valueColorMin: e.target.value })}
                                >
                                  {MAP_MARKER_COLORS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: opt.value, border: '1px solid #ccc' }} />
                                        {opt.label}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Color valor alto (máximo)</InputLabel>
                                <Select
                                  value={mapConfig.valueColorMax || '#f44336'}
                                  label="Color valor alto (máximo)"
                                  onChange={(e) => setMapConfig({ valueColorMax: e.target.value })}
                                >
                                  {MAP_MARKER_COLORS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: opt.value, border: '1px solid #ccc' }} />
                                        {opt.label}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Apariencia del marcador</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!mapConfig.heatmapMode}
                            onChange={(e) => setMapConfig({ heatmapMode: e.target.checked })}
                          />
                        }
                        label="Mostrar como mapa de calor por defecto"
                      />
                    </Grid>
                    {mapConfig.valueColumn && (
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                            checked={!!mapConfig.heatmapUseValue}
                            onChange={(e) => setMapConfig({ heatmapUseValue: e.target.checked })}
                            />
                          }
                          label="En mapa de calor: ponderar por valor (zonas con valores más altos se ven más calientes)"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Estilo</InputLabel>
                        <Select
                          value={mapConfig.markerStyle || 'circle'}
                          label="Estilo"
                          onChange={(e) => setMapConfig({ markerStyle: e.target.value })}
                        >
                          {MAP_MARKER_STYLES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Color</InputLabel>
                        <Select
                          value={mapConfig.markerColor || '#1976d2'}
                          label="Color"
                          onChange={(e) => setMapConfig({ markerColor: e.target.value })}
                        >
                          {MAP_MARKER_COLORS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: opt.value, border: '1px solid #ccc' }} />
                                {opt.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tamaño</InputLabel>
                        <Select
                          value={mapConfig.markerSize || 'medium'}
                          label="Tamaño"
                          onChange={(e) => setMapConfig({ markerSize: e.target.value })}
                        >
                          {MAP_MARKER_SIZES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                {chartType !== 'table' && chartType !== 'heatmapGrid' && chartType !== 'map' && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Columnas X/Y, agrupación, leyenda y ejes: panel <strong>Ejes y series</strong> junto a la vista previa.
                    </Typography>
                  </Grid>
                )}
                  </Grid>
                  </Box>
                )}

                {tabValue === 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Título del gráfico - Siempre visible */}
              <TextField
                label="Título del gráfico"
                value={advancedConfig.title}
                onChange={(e) => handleAdvancedConfigChange('title', e.target.value)}
                fullWidth
                size="small"
                placeholder="Opcional"
              />

              {/* Opciones específicas para tabla */}
              {chartType === 'table' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Totales
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={advancedConfig.tableShowTotalColumn === true}
                              onChange={(e) => handleAdvancedConfigChange('tableShowTotalColumn', e.target.checked)}
                            />
                          }
                          label="Agregar columna total (suma por fila)"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={advancedConfig.tableShowTotalRow === true}
                              onChange={(e) => handleAdvancedConfigChange('tableShowTotalRow', e.target.checked)}
                            />
                          }
                          label="Agregar fila total (suma por columna)"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}

              {/* Opciones específicas para el carrusel de imágenes */}
              {chartType === 'imageCarousel' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Carrusel de imágenes
                    </Typography>
                    <Tooltip title="Filtra por el valor de class en cada detección" placement="right">
                      <Box sx={{ width: '100%', mb: 1.25 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Filtrar por clase (opcional)"
                          value={advancedConfig.imageCarouselConfig?.cropClassFilter || ''}
                          onChange={(e) =>
                            handleAdvancedConfigChange('imageCarouselConfig', {
                              ...(advancedConfig.imageCarouselConfig || {}),
                              cropClassFilter: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Tooltip>
                    <Box
                      sx={{
                        pl: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                        '& .MuiFormControlLabel-label': { fontSize: '0.75rem', lineHeight: 1.35 },
                      }}
                    >
                      <FormControlLabel
                        sx={{ ml: 0, alignItems: 'flex-start' }}
                        control={
                          <Switch
                            size="small"
                            checked={advancedConfig.imageCarouselConfig?.singleImageMode !== false}
                            onChange={(e) =>
                              handleAdvancedConfigChange('imageCarouselConfig', {
                                ...(advancedConfig.imageCarouselConfig || {}),
                                singleImageMode: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Una imagen a la vez (avance automático cada 5 segundos)"
                      />
                      <Tooltip
                        title="Muestra solo el recorte de cada detección (una miniatura por caja) en vez de la foto completa con las cajas superpuestas."
                        placement="right"
                      >
                        <FormControlLabel
                          sx={{ ml: 0, alignItems: 'flex-start', width: 'fit-content' }}
                          control={
                            <Switch
                              size="small"
                              checked={advancedConfig.imageCarouselConfig?.cropMode === true}
                              onChange={(e) =>
                                handleAdvancedConfigChange('imageCarouselConfig', {
                                  ...(advancedConfig.imageCarouselConfig || {}),
                                  cropMode: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Mostrar solo el recorte de cada detección"
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                </>
              )}

              {/* Solo mostrar leyenda y otras opciones si NO es tabla */}
              {chartType !== 'table' && (
                <>
                  <Divider />

                  {/* Configuración de leyenda */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Leyenda
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.legendDisplay}
                              onChange={(e) => handleAdvancedConfigChange('legendDisplay', e.target.checked)}
                            />
                          }
                          label="Mostrar leyenda"
                        />
                      </Grid>
                      {advancedConfig.legendDisplay && (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Posición de la leyenda</InputLabel>
                            <Select
                              value={advancedConfig.legendPosition}
                              label="Posición de la leyenda"
                              onChange={(e) => handleAdvancedConfigChange('legendPosition', e.target.value)}
                            >
                              {isPieChart && <MenuItem value="left">Izquierda</MenuItem>}
                              <MenuItem value="top">Arriba</MenuItem>
                              <MenuItem value="bottom">Abajo</MenuItem>
                              {!isPieChart && <MenuItem value="left">Izquierda</MenuItem>}
                              <MenuItem value="right">Derecha</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </>
              )}

              {chartSupportsFillTimeAxisGaps && (chartType === 'line' || chartType === 'bar') && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Eje temporal
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.fillTimeAxisGaps === true}
                              onChange={(e) =>
                                handleAdvancedConfigChange('fillTimeAxisGaps', e.target.checked)
                              }
                            />
                          }
                          label="Incluir horas/fechas sin datos en el eje X"
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5, mt: -0.5 }}>
                          Rellena el intervalo entre el primer y último dato para que los huecos sin registros
                          ocupen su espacio temporal real.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}

              {chartType === 'bar' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Etiquetas en barras
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.barShowTotalLabel === true}
                              onChange={(e) => handleAdvancedConfigChange('barShowTotalLabel', e.target.checked)}
                            />
                          }
                          label="Mostrar total de la barra arriba de cada barra"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.barShowSegmentLabel === true}
                              onChange={(e) => handleAdvancedConfigChange('barShowSegmentLabel', e.target.checked)}
                            />
                          }
                          label="Mostrar subtotal de cada segmento (leyenda) dentro de su zona"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}

              {chartType === 'line' && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Líneas
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Interpolación</InputLabel>
                          <Select
                            value={advancedConfig.lineInterpolation || 'linear'}
                            label="Interpolación"
                            onChange={(e) =>
                              handleAdvancedConfigChange('lineInterpolation', e.target.value)
                            }
                          >
                            {LINE_INTERPOLATION_OPTIONS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.lineFillMissingAsZero !== false}
                              onChange={(e) =>
                                handleAdvancedConfigChange('lineFillMissingAsZero', e.target.checked)
                              }
                            />
                          }
                          label="Rellenar huecos con 0"
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4.5, mt: -0.5 }}>
                          Activo: 0 en horas/fechas vacías (con eje temporal completo) y donde falta una serie.
                          Desactivado: null en esos puntos; la línea no conecta a través del hueco.
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.lineShowValueLabel === true}
                              onChange={(e) => handleAdvancedConfigChange('lineShowValueLabel', e.target.checked)}
                            />
                          }
                          label="Mostrar valor en cada punto"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}

              {isPieChart && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Etiquetas en pastel / dona
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ubicación etiquetas</InputLabel>
                          <Select
                            value={advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION}
                            label="Ubicación etiquetas"
                            onChange={(e) => handleAdvancedConfigChange('pieLabelPosition', e.target.value)}
                          >
                            {PIE_LABEL_POSITION_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.pieShowValueLabel !== false}
                              onChange={(e) => handleAdvancedConfigChange('pieShowValueLabel', e.target.checked)}
                            />
                          }
                          label={
                            isPieLegendLabelMode(advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION)
                              ? 'Mostrar valor numérico en la leyenda'
                              : 'Mostrar valor numérico'
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={advancedConfig.pieShowPercentLabel !== false}
                              onChange={(e) => handleAdvancedConfigChange('pieShowPercentLabel', e.target.checked)}
                            />
                          }
                          label={
                            isPieLegendLabelMode(advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION)
                              ? 'Mostrar porcentaje en la leyenda'
                              : 'Mostrar porcentaje'
                          }
                        />
                      </Grid>
                      {(advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION) === 'legendAndChart' && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            En el gráfico solo se etiquetan segmentos grandes (≥7%) para evitar solapamiento.
                            En segmentos pequeños queda solo la leyenda.
                          </Typography>
                        </Grid>
                      )}
                      {(advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION) === 'outside' && (
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={advancedConfig.pieShowNameLabel === true}
                                onChange={(e) => handleAdvancedConfigChange('pieShowNameLabel', e.target.checked)}
                              />
                            }
                            label="Mostrar nombre en callout"
                          />
                        </Grid>
                      )}
                      {isPieLegendLabelMode(advancedConfig.pieLabelPosition || DEFAULT_PIE_LABEL_POSITION) && (
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Tamaño de la leyenda</InputLabel>
                            <Select
                              value={advancedConfig.pieLegendFontSize || 'lg'}
                              label="Tamaño de la leyenda"
                              onChange={(e) => handleAdvancedConfigChange('pieLegendFontSize', e.target.value)}
                            >
                              {PIE_LEGEND_FONT_SIZE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </>
              )}

              {/* Filtro dinámico */}
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Filtros dinámicos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Puedes definir uno o más parámetros (ej. @Producto, @Vendedor). Si un parámetro también está en filtros globales del dashboard, aquí se mostrará como global y se respetará ese valor.
                </Typography>
                {embedded && queryDynamicFilterConfig?.enabled && queryDynamicFilterConfig?.paramName && dynamicFilters.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Sugerencia de la query actual: @{queryDynamicFilterConfig.paramName}
                  </Typography>
                )}
                <Grid container spacing={1.5}>
                  {dynamicFilters.map((df, idx) => (
                    <Grid item xs={12} key={`df-${idx}`}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={df.enabled === true}
                              onChange={(e) => {
                                const next = [...dynamicFilters];
                                next[idx] = { ...next[idx], enabled: e.target.checked };
                                setDynamicFilters(next);
                              }}
                            />
                          }
                          label={`Filtro ${idx + 1}`}
                        />
                        <TextField
                          label="Parámetro (@param)"
                          value={df.paramName || ''}
                          onChange={(e) => {
                            const next = [...dynamicFilters];
                            next[idx] = { ...next[idx], paramName: e.target.value };
                            setDynamicFilters(next);
                          }}
                          size="small"
                          sx={{ minWidth: 220 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const next = dynamicFilters.filter((_, i) => i !== idx);
                            setDynamicFilters(next);
                          }}
                          title="Quitar filtro"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                        {df.filterType === 'date' ? (
                          <TextField
                            size="small"
                            type="date"
                            label="Fecha para previsualización"
                            value={df.selectedValue ? String(df.selectedValue).slice(0, 10) : ''}
                            onChange={(e) => {
                              const next = [...dynamicFilters];
                              next[idx] = { ...next[idx], selectedValue: e.target.value };
                              setDynamicFilters(next);
                            }}
                            sx={{ minWidth: 220 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        ) : (
                          <Autocomplete
                            size="small"
                            sx={{ minWidth: 280, flex: 1 }}
                            options={dynamicFilterPreviewOptions[`${idx}::${String(df?.paramName || '').trim()}`] || []}
                            value={(dynamicFilterPreviewOptions[`${idx}::${String(df?.paramName || '').trim()}`] || []).find((o) => String(o.value) === String(df?.selectedValue ?? '')) ?? null}
                            getOptionLabel={(opt) => (opt && (opt.label != null ? String(opt.label) : String(opt.value))) || ''}
                            isOptionEqualToValue={(opt, val) => opt && val && String(opt.value) === String(val.value)}
                            onChange={(_, newVal) => {
                              const next = [...dynamicFilters];
                              next[idx] = { ...next[idx], selectedValue: newVal?.value ?? '' };
                              setDynamicFilters(next);
                            }}
                            disabled={!df?.enabled || !(dynamicFilterPreviewOptions[`${idx}::${String(df?.paramName || '').trim()}`] || []).length}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={dynamicFilterPreviewLoading[`${idx}::${String(df?.paramName || '').trim()}`] ? 'Cargando opciones…' : 'Valor para previsualización'}
                                size="small"
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {dynamicFilterPreviewLoading[`${idx}::${String(df?.paramName || '').trim()}`] ? <CircularProgress color="inherit" size={14} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            noOptionsText="Sin opciones"
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() =>
                        setDynamicFilters([
                          ...dynamicFilters,
                          { ...DEFAULT_DYNAMIC_FILTER, enabled: true, allowExecuteWithoutSelection: true },
                        ])
                      }
                    >
                      Agregar filtro dinámico
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {(chartType === 'line' || chartType === 'scatter') && (
                <>
                  <Divider />

                  {/* Configuración de puntos */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Puntos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Radio de puntos"
                          value={advancedConfig.pointRadius}
                          onChange={(e) => handleAdvancedConfigChange('pointRadius', e.target.value)}
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Auto (3)"
                          inputProps={{ min: 0, max: 20, step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Ancho del borde"
                          value={advancedConfig.borderWidth}
                          onChange={(e) => handleAdvancedConfigChange('borderWidth', e.target.value)}
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Auto (2)"
                          inputProps={{ min: 0, max: 10, step: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}

              </Box>
                )}
              </Box>
            </Box>

            {/* Panel previsualización: ejes 20% izquierda + gráfico 80% derecha */}
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 280 }}>
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Vista Previa
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'row',
                  minHeight: 0,
                  gap: 1,
                  p: 1,
                }}
              >
                {previewAxesSeriesPanel}
                <Box sx={{ width: '80%', flex: '1 1 80%', overflow: 'auto', p: 1, position: 'relative', minWidth: 0 }}>
                {!canPreview ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: isHeatmapChart ? 320 : '100%', minHeight: 240 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {previewData.length === 0 
                        ? 'No hay datos para previsualizar'
                        : chartType === 'table'
                        ? 'La tabla se mostrará aquí'
                        : chartType === 'heatmapGrid'
                        ? 'Define columnas y grilla en el panel lateral. Luego haz clic en cada celda para asignar el valor del filtro.'
                        : chartType === 'map'
                        ? 'Selecciona lat/lng o dirección en el panel lateral.'
                        : 'Selecciona columnas X e Y en el panel lateral para ver la previsualización'}
                    </Typography>
                  </Box>
                ) : chartType === 'map' ? (
                  <Paper sx={{ height: 320, position: 'relative' }}>
                    <MapWidget data={previewData} mapConfig={mapConfig} getAuthHeaders={getAuthHeaders} />
                  </Paper>
                ) : chartType === 'heatmapGrid' ? (
                  <Paper sx={{ height: 320, position: 'relative' }}>
                    <HeatmapGridWidget
                      data={previewData}
                      heatmapGridConfig={heatmapGridConfig}
                      editMode
                      availableColumns={columns}
                      onCellAssignmentChange={(r, c, assignment) => {
                        setHeatmapGridConfig((prev) => {
                          const nextAssignments = { ...(prev.cellAssignments || {}) };
                          const key = `${r},${c}`;
                          if (assignment) nextAssignments[key] = assignment;
                          else delete nextAssignments[key];
                          return { ...prev, cellAssignments: nextAssignments };
                        });
                      }}
                    />
                  </Paper>
                ) : chartType === 'table' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 300 }}>
                    {advancedConfig.title && (
                      <Typography variant="h6" gutterBottom>
                        {advancedConfig.title}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                      <DataTable
                        data={previewData}
                        showTotalColumn={advancedConfig.tableShowTotalColumn}
                        showTotalRow={advancedConfig.tableShowTotalRow}
                        visibleColumns={
                          resolveCommittedHeatmapPositions(value || chart, previewData) &&
                          advancedConfig.tableVisibleColumns
                            ? appendHeatmapPositionVisibleColumns(advancedConfig.tableVisibleColumns)
                            : advancedConfig.tableVisibleColumns ?? undefined
                        }
                        columnDecimals={advancedConfig.tableColumnDecimals ?? undefined}
                        columnDateFormats={advancedConfig.tableColumnDateFormats ?? undefined}
                        columnDateBuckets={advancedConfig.tableColumnDateBuckets ?? undefined}
                        columnColorThresholds={advancedConfig.tableColumnColorThresholds ?? undefined}
                        columnLabels={advancedConfig.tableColumnLabels ?? {}}
                        columnImageColumns={advancedConfig.tableColumnImageColumns ?? undefined}
                        calculatedColumns={advancedConfig.tableCalculatedColumns || []}
                        columnFilters={advancedConfig.tableColumnFilters ?? {}}
                        onColumnFilterChange={(col, values) => setAdvancedConfig((prev) => ({
                          ...prev,
                          tableColumnFilters: { ...(prev.tableColumnFilters || {}), [col]: values },
                        }))}
                        onClearAllFilters={() => setAdvancedConfig((prev) => ({ ...prev, tableColumnFilters: {} }))}
                      />
                    </Box>
                    {previewData.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5 }}>
                        Mostrando {previewData.length} filas
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Paper sx={{ height: '100%', position: 'relative', minHeight: 400 }}>
                    <ChartWidget
                      type={chartType}
                      data={previewData}
                      xColumn={xColumn}
                      yColumn={yColumn}
                      groupByColumn={groupByColumn || undefined}
                      labelColumn={labelColumn || undefined}
                      xDateBucket={embedded ? resolveXDateBucket(value) : undefined}
                      xDateDisplayFormat={embedded ? resolveXDateDisplayFormat(value) : undefined}
                      labelDateBucket={embedded ? resolveLabelDateBucket(value) : undefined}
                      labelDateDisplayFormat={embedded ? resolveLabelDateDisplayFormat(value) : undefined}
                      stacked={chartType === 'bar' && (groupByColumn || labelColumn) ? stacked : undefined}
                      advancedConfig={advancedConfig}
                      heatmapGridConfig={chartType === 'heatmapGrid' ? heatmapGridConfig : undefined}
                      mapConfig={chartType === 'map' ? mapConfig : undefined}
                      imageCarouselConfig={chartType === 'imageCarousel' ? resolveImageCarouselConfig({ advancedConfig }, previewData) : undefined}
                      getAuthHeaders={chartType === 'map' || chartType === 'imageCarousel' ? getAuthHeaders : undefined}
                      label={advancedConfig.title || 'Vista Previa'}
                    />
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>
          </Box>
  );

  if (embedded) return innerContent;

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle>Configurar gráfico / Vista previa</DialogTitle>
        <DialogContent>{innerContent}</DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={chartType === 'table' || chartType === 'imageCarousel' ? false : chartType === 'heatmapGrid' ? !(Number(heatmapGridConfig?.gridRows) >= 1 && Number(heatmapGridConfig?.gridCols) >= 1 && heatmapGridConfig?.valueColumn && heatmapGridConfig?.filterColumn) : chartType === 'map' ? !((mapConfig?.latColumn && mapConfig?.lngColumn) || mapConfig?.addressColumn) : (!xColumn || !yColumn)}
          >
            {mode === 'preview' ? 'Aplicar' : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ChartEditDialog);
